import { Component, computed, model, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { UserProfileHeaderComponent } from '../../components/user-profile-header/user-profile-header.component';
import { UserModel } from '../../../../core/models/data/user.model';
import { UserService } from '../../../../core/services/user.service';
import { OrganizationListSortOptions } from '../../../../core/enums/sort-options.enums';
import { ProfileConstants } from '../../../../core/constants/profile.constants';
import { MemberStatuses, PLATFORM_ROLE_PRECEDENCE, UserStatuses } from '../../../../core/enums/data.enums';
import { OrganizationService } from '../../../../core/services/organization.service';
import { OrganizationsFilter } from '../../../../core/filters/organizations.filter';
import { MemberService } from '../../../../core/services/member.service';
import { UserSecurityComponent } from "../../components/user-security/user-security.component";
import { UserSettingsComponent } from "../../components/user-settings/user-settings.component";
import { UserMembershipModel } from '../../../../core/models/ui/user-membership.model';
import { AuthorizationModel } from '../../../../core/models/data/authorization.model';
import { CommonChecks } from '../../../../core/utilities/common-checks.utilities';
import { UserProfileLogsComponent } from '../../components/user-profile-logs/user-profile-logs.component';
import { UserProfileOverviewComponent } from '../../components/user-profile-overview/user-profile-overview.component';
import { BaseProfilePageClass } from '../../../../core/base-classes/base-profile-page-class';

@Component({
  selector: 'ms-user-profile.page',
  imports: [CommonModule, FontAwesomeModule, UserProfileHeaderComponent, UserSecurityComponent, UserSettingsComponent, UserProfileLogsComponent, UserProfileOverviewComponent],
  templateUrl: './user-profile.page.html',
})

export class UserProfilePage extends BaseProfilePageClass implements OnInit, OnDestroy {
  public user = model<UserModel | undefined>(undefined);
  public isLoading = signal<boolean>(false);
  public userId: number | undefined = undefined;
  private previousUserId: number | undefined = undefined;
  public organizationsFilter: OrganizationsFilter = {};
  public organizationMemberships: UserMembershipModel[] = [];
  public userRolesAndPermissions = signal<AuthorizationModel | undefined>(undefined);

  // The viewed user's own platform role, shown in the profile header (DEV-2598). Note this is the
  // *subject's* role, not the viewer's — the isStaff/ifHasPermission directives read the logged-in
  // user's authorization state and would gate on the wrong person here. userPermissions comes back
  // null (not an empty object) for a user holding no platform role, hence the optional chaining.
  public platformRole = computed<string | undefined>(() => {
    const roles = this.userRolesAndPermissions()?.userPermissions?.roles ?? [];
    return PLATFORM_ROLE_PRECEDENCE.find(role => roles.includes(role));
  });

  public orderBy: OrganizationListSortOptions = OrganizationListSortOptions.OrganizationNameASC;
  public skip: number = 0;
  public take: number = 10;

  @ViewChild(UserProfileOverviewComponent) userOverviewRef?: UserProfileOverviewComponent;

  private destroy$ = new Subject<void>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private organizationService: OrganizationService,
    private memberService: MemberService
  ) {
    super();
  }

  ngOnInit(): void {
    this.navItems.set(ProfileConstants.USER_PROFILE_NAV_ITEMS());

    // Jake
    // This pattern solves for a niche case where
    // a user goes to someone elses profile, then
    // trys to access their own. This code prevents
    // the Ids from colliding. The subscription ensures the
    // newest instance of the Id is used
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newUserId = +(params.get('userId') ?? NaN) || undefined;
        const isNavigation = this.previousUserId !== undefined && newUserId !== this.previousUserId;

        this.userId = newUserId;
        this.previousUserId = newUserId;

        if (isNavigation) {
          this.setActiveTab(0);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        this.loadUserProfile();
      });

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const tab = params.get('tab') || '';
        if (tab) {
          this.setTabFromUrl(tab);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadUserProfile(): Promise<void> {
    this.isLoading.set(true);
    this.user.set(undefined);
    this.isEntityOwner.set(false);
    this.organizationMemberships = [];
    // Cleared alongside the user so navigating from one profile to another can't flash the
    // previous user's role in the header before the new fetch lands.
    this.userRolesAndPermissions.set(undefined);

    try {
      let response = await this.userService.getUserById(this.userId!);

      if (response.success === true) {
        const cleanedUser = {
          ...response.data,
          phoneNumber: response.data?.phoneNumber ?? ''
        } as UserModel;

        this.user.set(cleanedUser);
        this.isEntityOwner.set(this.userSessionService.isCurrentUser(cleanedUser.userId));

        await this.getUserRolesAndPermissions();
        await this.getOrganizationsByUserId();
        await this.getMembersByOrganizationId();
      } else {
        this.router.navigate(['system', '404']);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      this.router.navigate(['system', '404']);
    } finally {
      this.isLoading.set(false);
    }
  }

  public async getOrganizationsByUserId(): Promise<void> {
    try {
      this.isLoading.set(true);

      const response = await this.organizationService.searchOrganizations({ userIds: [this.userId!], memberStatuses: [MemberStatuses.Active] }, this.orderBy, this.skip, this.take);
      for (let org of response.data?.data || []) {
        this.organizationMemberships.push({organizationId: org.organizationId, organizationName: org.name});
      }
    } catch (error) {
      console.error('Error searching organizations by user ID:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  public async getUserRolesAndPermissions() : Promise<void> {
    try {
      this.isLoading.set(true);
      if (CommonChecks.isNullOrUndefined(this.user()?.userId) == false) {
        const response = await this.authorizationService.getUserRolesAndPermissions(this.user()?.userId!);
        this.userRolesAndPermissions.set(response.data);
      }      
    } catch (error) {
      console.error('Error getting user\'s permissions:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  public async getMembersByOrganizationId(): Promise<void> {
    this.isLoading.set(true);
    try {
      const memberIds = this.userRolesAndPermissions()?.memberPermissions?.map(mp => mp.memberId);

      if(!memberIds) {
        return;
      }

      for (const memberId of memberIds) {
        const response = (await this.memberService.getMemberById(memberId));

        if (response.success === true) {
          if (response.data) {
            const member = response.data;
            const idx = this.organizationMemberships.findIndex(org => org.organizationId === member.organizationId);
            const role = this.userRolesAndPermissions()?.memberPermissions?.find(x => x.organizationId === member.organizationId)?.roles?.[0] ?? "";

            if (idx !== -1) {
              this.organizationMemberships[idx] = {
                ...this.organizationMemberships[idx],
                memberId: member.memberId,
                role: role,
                status: member.status,
                lastLogin: member.lastLogin
              };
            }
          }
        }
      }
    } catch (error) {
      console.error('Error searching members by user ID:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  public async onUserUpdated(): Promise<void> {
    await this.loadUserProfile();
    this.setActiveTab(0);
  }

  public onEditProfileRequested(): void {
    if (this.user()?.status === UserStatuses.Deleted) return;
    this.userOverviewRef?.openUpdateUserModal();
  }
}
